import { useState, useEffect, useRef, useCallback } from "react";
import { allExerciseNames as libraryNames, exerciseLibrary } from "./exerciseLibrary";
import { getExerciseVideoUrl } from "./exerciseVideoMap";
import { db } from "./firebase";
import { getWorkoutPrograms, EQUIPMENT_LIST } from "./workoutPrograms";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { saveBodyStats } from "./bodyMetrics";
import { recordAutoProgress, removeAutoProgress } from "./challenges";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ─── Strength Chart (per-session, with set stats below) ──────────────────────
export function ExerciseStrengthChart({ userId }) {
  const [exerciseHistory, setExerciseHistory] = useState({});
  const [exerciseStats, setExerciseStats] = useState({});
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseList, setExerciseList] = useState([]);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!userId) return;
    async function load() {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, "users", userId, "workoutLogs"), orderBy("completedAt", "asc"))
        );
        // Per-session aggregates for chart + per-exercise lifetime stats
        const history = {};
        const stats = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          const dateStr = data.completedAt?.toDate
            ? data.completedAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : "?";
          (data.exercises || []).forEach((ex) => {
            if (!ex.name) return;
            if (!history[ex.name]) history[ex.name] = [];
            if (!stats[ex.name]) stats[ex.name] = { totalSets: 0, totalReps: 0, totalVolume: 0, bestSet: null, sessions: 0 };

            const sets = (ex.sets || []).filter((s) => parseInt(s.reps) > 0 || parseFloat(s.weight) > 0);
            const maxWeight = sets.reduce((m, s) => Math.max(m, parseFloat(s.weight) || 0), 0);
            const totalReps = sets.reduce((sum, s) => sum + (parseInt(s.reps) || 0), 0);
            const volume = sets.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (parseInt(s.reps) || 0)), 0);
            const isBodyweight = sets.length > 0 && maxWeight === 0;

            // Include if there are any reps logged (bodyweight = weight 0 is valid)
            if (totalReps > 0 || maxWeight > 0) {
              history[ex.name].push({
                date: dateStr,
                weight: isBodyweight ? null : maxWeight,
                reps: totalReps,
                sets: sets.length,
                isBodyweight,
              });
            }

            // Lifetime stats
            stats[ex.name].totalSets += sets.length;
            stats[ex.name].totalReps += totalReps;
            stats[ex.name].totalVolume += volume;
            stats[ex.name].sessions += 1;
            sets.forEach((s) => {
              const w = parseFloat(s.weight) || 0;
              const r = parseInt(s.reps) || 0;
              if (!stats[ex.name].bestSet || w > stats[ex.name].bestSet.weight || (w === stats[ex.name].bestSet.weight && r > stats[ex.name].bestSet.reps)) {
                stats[ex.name].bestSet = { weight: w, reps: r };
              }
            });
          });
        });
        const names = Object.keys(history);
        setExerciseHistory(history);
        setExerciseStats(stats);
        setExerciseList(names);
        setSelectedExercise(names[0] || null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  function swipe(dir) {
    if (!exerciseList.length) return;
    const idx = exerciseList.indexOf(selectedExercise);
    const next = (idx + dir + exerciseList.length) % exerciseList.length;
    setSelectedExercise(exerciseList[next]);
  }

  if (loading) return <div style={{ color: "#6b8cb0", fontSize: "13px", padding: "12px 0" }}>Loading strength data…</div>;
  if (!exerciseList.length) return (
    <div style={{ color: "#6b8cb0", fontSize: "13px", padding: "12px 0", textAlign: "center" }}>
      No exercise data yet. Log a workout to see your strength progress here.
    </div>
  );

  const chartData = exerciseHistory[selectedExercise] || [];
  const stat = exerciseStats[selectedExercise] || {};
  const avgSets = stat.sessions > 0 ? (stat.totalSets / stat.sessions).toFixed(1) : "—";

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div style={{ background: "#fff", border: "1.5px solid #bfdbfe", borderRadius: "10px", padding: "8px 12px", fontSize: "12px", boxShadow: "0 4px 12px rgba(30,58,138,0.12)" }}>
        <div style={{ fontWeight: "bold", color: "#1e3a5f", marginBottom: "4px" }}>{d?.date}</div>
        {d?.isBodyweight
          ? <div style={{ color: "#2563eb" }}>⚖️ Bodyweight</div>
          : <div style={{ color: "#2563eb" }}>⚖️ Max weight: {d?.weight} lbs</div>
        }
        <div style={{ color: "#7c3aed" }}>🔁 Total reps: {d?.reps}</div>
        <div style={{ color: "#4b6b8a" }}>📋 Sets: {d?.sets}</div>
      </div>
    );
  };

  return (
    <div>
      {/* Exercise selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <button onClick={() => swipe(-1)} style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: "#1e3a5f", fontWeight: "bold", fontSize: "16px" }}>‹</button>
        <select
          value={selectedExercise || ""}
          onChange={(e) => setSelectedExercise(e.target.value)}
          style={{ flex: 1, padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px", fontWeight: "bold" }}
        >
          {exerciseList.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <button onClick={() => swipe(1)} style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: "#1e3a5f", fontWeight: "bold", fontSize: "16px" }}>›</button>
      </div>

      {/* Chart — one point per session */}
      <div
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 40) swipe(diff > 0 ? 1 : -1);
          touchStartX.current = null;
        }}
      >
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#dbeafe" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b8cb0" }} />
            <YAxis yAxisId="weight" orientation="left" tick={{ fontSize: 10, fill: "#2563eb" }} label={{ value: "lbs", angle: -90, position: "insideLeft", offset: 14, style: { fontSize: 9, fill: "#2563eb" } }} />
            <YAxis yAxisId="reps" orientation="right" tick={{ fontSize: 10, fill: "#7c3aed" }} label={{ value: "reps", angle: 90, position: "insideRight", offset: 14, style: { fontSize: 9, fill: "#7c3aed" } }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Bar yAxisId="reps" dataKey="reps" fill="#c4b5fd" name="total reps" radius={[4, 4, 0, 0]} />
            <Line yAxisId="weight" type="monotone" dataKey="weight" stroke="#2563eb" strokeWidth={2} dot={{ r: 3, fill: "#2563eb" }} name="max weight (lbs)" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Set stats summary row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "14px" }}>
        {[
          { label: "Avg sets/session", value: avgSets },
          { label: "Best set", value: stat.bestSet ? (stat.bestSet.weight > 0 ? `${stat.bestSet.reps}×${stat.bestSet.weight}lbs` : `${stat.bestSet.reps} reps BW`) : "—" },
          { label: "Total volume", value: stat.totalVolume > 0 ? `${stat.totalVolume.toLocaleString()} lbs` : "—" },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: "#f0f9ff", borderRadius: "12px", padding: "10px 8px", textAlign: "center", border: "1.5px solid #bfdbfe" }}>
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#1e3a5f" }}>{value}</div>
            <div style={{ fontSize: "10px", color: "#6b8cb0", marginTop: "2px" }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: "10px", color: "#6b8cb0", textAlign: "center", marginTop: "8px" }}>
        Each point = one session · swipe or use arrows to switch exercises
      </div>
    </div>
  );
}

// ─── Ding sound via Web Audio API ────────────────────────────────────────────
function playDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Two-tone ding: high then slightly lower
    [[880, 0, 0.15], [1100, 0.18, 0.15], [880, 0.36, 0.2]].forEach(([freq, start, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });
  } catch (e) {
    // Audio not available — silently ignore
  }
}

// ─── Rest Timer Hook ─────────────────────────────────────────────────────────
function useRestTimer() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(60);
  const intervalRef = useRef(null);

  const stop = useCallback(() => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setSecondsLeft(0);
  }, []);

  const start = useCallback((secs) => {
    clearInterval(intervalRef.current);
    const duration = secs ?? totalSeconds;
    setTotalSeconds(duration);
    setSecondsLeft(duration);
    setIsRunning(true);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setIsRunning(false);
          // Ding + vibrate on finish
          playDing();
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [totalSeconds]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  return { secondsLeft, isRunning, totalSeconds, start, stop, setTotalSeconds };
}

// ─── Workout Elapsed Timer ────────────────────────────────────────────────────
function WorkoutElapsedTimer({ startedAt }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

// ─── Global Rest Timer Bar ────────────────────────────────────────────────────
function GlobalRestTimer({ timer, workoutStartedAt }) {
  const { secondsLeft, isRunning, totalSeconds, start, stop, setTotalSeconds } = timer;
  const presets = [30, 60, 90, 120];
  const progress = totalSeconds > 0 ? secondsLeft / totalSeconds : 0;
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;

  return (
    <div style={{
      background: "#f0f9ff",
      borderRadius: "16px",
      padding: "12px 14px",
      border: "2px solid #bfdbfe",
      marginBottom: "14px",
    }}>
      {/* Top row: elapsed + rest countdown */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ fontSize: "12px", color: "#6b8cb0" }}>
          ⏱ Workout: <strong style={{ color: "#1e3a5f" }}><WorkoutElapsedTimer startedAt={workoutStartedAt} /></strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "11px", color: "#6b8cb0" }}>Rest:</span>
          <span style={{
            fontSize: "22px",
            fontWeight: "bold",
            color: isRunning ? (secondsLeft <= 5 ? "#ef4444" : "#2563eb") : "#1e3a5f",
            fontVariantNumeric: "tabular-nums",
            minWidth: "52px",
            textAlign: "right",
          }}>
            {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div style={{ height: "6px", background: "#dbeafe", borderRadius: "4px", marginBottom: "8px", overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${progress * 100}%`,
            background: secondsLeft <= 5 ? "#ef4444" : "#2563eb",
            borderRadius: "4px",
            transition: "width 1s linear, background 0.3s",
          }} />
        </div>
      )}

      {/* Preset buttons + stop */}
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {presets.map((sec) => (
          <button
            key={sec}
            type="button"
            onClick={() => start(sec)}
            style={{
              flex: 1,
              padding: "6px 4px",
              borderRadius: "8px",
              border: totalSeconds === sec && isRunning ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: totalSeconds === sec && isRunning ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f",
              fontWeight: "bold",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            {sec < 60 ? `${sec}s` : `${sec / 60}m`}
          </button>
        ))}
        {isRunning && (
          <button
            type="button"
            onClick={stop}
            style={{ padding: "6px 10px", borderRadius: "8px", border: "2px solid #fca5a5", background: "#fff1f2", color: "#ef4444", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
          >✕</button>
        )}
      </div>

      {/* Custom time input */}
      <div style={{ display: "flex", gap: "6px", marginTop: "8px", alignItems: "center" }}>
        <span style={{ fontSize: "11px", color: "#6b8cb0", whiteSpace: "nowrap" }}>Custom:</span>
        <input
          id="custom-timer-input"
          type="number"
          min="5"
          max="600"
          placeholder="secs"
          defaultValue=""
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const v = parseInt(e.target.value);
              if (v > 0) { start(v); e.target.value = ""; }
            }
          }}
          style={{ width: "60px", padding: "5px 8px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "12px" }}
        />
        <button
          type="button"
          onClick={() => {
            const input = document.getElementById("custom-timer-input");
            const v = parseInt(input?.value);
            if (v > 0) { start(v); if (input) input.value = ""; }
          }}
          style={{ padding: "5px 10px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
        >▶</button>
      </div>
    </div>
  );
}

// ─── Per-Exercise Rest Timer ──────────────────────────────────────────────────
function ExerciseRestTimer({ globalTimer, defaultRestSecs = 60, onRestSecsChange }) {
  const [restSecs, setRestSecs] = useState(defaultRestSecs);
  const [localRunning, setLocalRunning] = useState(false);
  const [localLeft, setLocalLeft] = useState(0);
  const intervalRef = useRef(null);

  // Sync if parent changes default (e.g. exercise name changes)
  useEffect(() => {
    setRestSecs(defaultRestSecs);
  }, [defaultRestSecs]);

  function handleRestSecsChange(val) {
    setRestSecs(val);
    onRestSecsChange?.(val);
  }

  function startLocal(secs) {
    clearInterval(intervalRef.current);
    const duration = secs ?? restSecs;
    setLocalLeft(duration);
    setLocalRunning(true);
    globalTimer.start(duration);
    intervalRef.current = setInterval(() => {
      setLocalLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setLocalRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function stopLocal() {
    clearInterval(intervalRef.current);
    setLocalRunning(false);
    setLocalLeft(0);
    globalTimer.stop();
  }

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const m = Math.floor(localLeft / 60);
  const s = localLeft % 60;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
      <span style={{ fontSize: "11px", color: "#6b8cb0", fontWeight: "bold" }}>REST:</span>

      <select
        value={restSecs}
        onChange={(e) => handleRestSecsChange(Number(e.target.value))}
        style={{ padding: "4px 6px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "12px" }}
      >
        {[20, 30, 45, 60, 90, 120, 180].map((v) => (
          <option key={v} value={v}>{v < 60 ? `${v}s` : `${v / 60}m`}</option>
        ))}
      </select>

      {localRunning ? (
        <>
          <span style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: localLeft <= 5 ? "#ef4444" : "#2563eb",
            fontVariantNumeric: "tabular-nums",
            minWidth: "40px",
          }}>
            {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={stopLocal}
            style={{ padding: "4px 8px", borderRadius: "8px", border: "1.5px solid #fca5a5", background: "#fff1f2", color: "#ef4444", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
          >✕ Stop</button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => startLocal(restSecs)}
          style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
        >▶ Start Rest</button>
      )}
    </div>
  );
}

// ─── Set Row (reps × weight, pre-fill from last set) ─────────────────────────
function SetRow({ set, idx, onChange, onRemove }) {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
      <span style={{ fontSize: "12px", color: "#6b8cb0", minWidth: "20px" }}>#{idx + 1}</span>
      <input
        type="number"
        placeholder="reps"
        value={set.reps}
        onChange={(e) => onChange({ ...set, reps: e.target.value })}
        style={{ width: "60px", padding: "6px 8px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "13px" }}
      />
      <span style={{ fontSize: "12px", color: "#6b8cb0" }}>×</span>
      <input
        type="number"
        placeholder="lbs"
        value={set.weight}
        onChange={(e) => onChange({ ...set, weight: e.target.value })}
        style={{ width: "60px", padding: "6px 8px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "13px" }}
      />
      <span style={{ fontSize: "11px", color: "#6b8cb0" }}>lbs</span>
      <button onClick={onRemove} style={{ background: "none", border: "none", color: "#f87171", fontSize: "16px", cursor: "pointer", padding: "0 4px" }}>✕</button>
    </div>
  );
}

// ─── Exercise Block with name autocomplete + pre-fill ────────────────────────
function ExerciseBlock({ exercise, onChange, onRemove, allExerciseNames, previousSets, videoGender, exerciseNicknames = {}, globalTimer }) {
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Build suggestions: match official name OR nickname against typed text
  const query = exercise.name.toLowerCase();
  const suggestions = exercise.name.length >= 1
    ? allExerciseNames
        .filter((n) => {
          const nick = (exerciseNicknames[n] || "").toLowerCase();
          const official = n.toLowerCase();
          if (official === query) return false;
          // Match if any word in the name starts with query, or name/nick contains query
          const wordStart = official.split(/\s+/).some((w) => w.startsWith(query));
          return wordStart || official.includes(query) || nick.includes(query);
        })
        .sort((a, b) => {
          const aOfficial = a.toLowerCase();
          const bOfficial = b.toLowerCase();
          const aNick = (exerciseNicknames[a] || "").toLowerCase();
          const bNick = (exerciseNicknames[b] || "").toLowerCase();

          // 1. Recently used (has previous sets) comes first
          const aRecent = Boolean(previousSets[a]);
          const bRecent = Boolean(previousSets[b]);
          if (aRecent && !bRecent) return -1;
          if (!aRecent && bRecent) return 1;

          // 2. Word-start matches before contains-only matches
          const aWordStart = aOfficial.split(/\s+/).some((w) => w.startsWith(query)) || aNick.split(/\s+/).some((w) => w.startsWith(query));
          const bWordStart = bOfficial.split(/\s+/).some((w) => w.startsWith(query)) || bNick.split(/\s+/).some((w) => w.startsWith(query));
          if (aWordStart && !bWordStart) return -1;
          if (!aWordStart && bWordStart) return 1;

          // 3. Alphabetical within each group
          return a.localeCompare(b);
        })
    : [];

  function addSet() {
    const lastSet = exercise.sets[exercise.sets.length - 1] || { reps: "", weight: "" };
    onChange({ ...exercise, sets: [...exercise.sets, { reps: lastSet.reps, weight: lastSet.weight }] });
  }

  function updateSet(i, updated) {
    onChange({ ...exercise, sets: exercise.sets.map((s, idx) => idx === i ? updated : s) });
  }

  function removeSet(i) {
    onChange({ ...exercise, sets: exercise.sets.filter((_, idx) => idx !== i) });
  }

  function selectSuggestion(officialName) {
    const prev = previousSets[officialName];
    const prevArr = prev?.sets || [];
    const sets = prevArr.length > 0
      ? prevArr.map((s) => ({ reps: s.reps || "", weight: s.weight || "" }))
      : [{ reps: "", weight: "" }];
    const restSecs = prev?.restSecs || exercise.restSecs || 60;
    onChange({ ...exercise, name: officialName, sets, restSecs });
    setShowSuggestions(false);
  }

  function handleNameChange(val) {
    onChange({ ...exercise, name: val });
    setShowSuggestions(true);
  }

  function handleNameBlur() {
    setTimeout(() => setShowSuggestions(false), 150);
  }

  function handleNameCommit(val) {
    const prev = previousSets[val];
    const prevArr = prev?.sets || [];
    if (prevArr.length > 0 && exercise.sets.every((s) => !s.reps && !s.weight)) {
      const restSecs = prev?.restSecs || exercise.restSecs || 60;
      onChange({ ...exercise, name: val, sets: prevArr.map((s) => ({ reps: s.reps || "", weight: s.weight || "" })), restSecs });
    }
  }

  const currentNickname = exerciseNicknames[exercise.name];

  return (
    <div style={{ background: "#f0f9ff", borderRadius: "14px", padding: "14px", marginBottom: "10px", border: "1.5px solid #bfdbfe", position: "relative" }}>
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px", alignItems: "center" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                placeholder="Exercise name or nickname"
                value={exercise.name}
                onChange={(e) => handleNameChange(e.target.value)}
                onBlur={(e) => { handleNameBlur(); handleNameCommit(e.target.value); }}
                onFocus={() => setShowSuggestions(true)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", fontWeight: "bold", boxSizing: "border-box" }}
              />
              {/* Show nickname badge if current name has one */}
              {currentNickname && (
                <div style={{ fontSize: "11px", color: "#2563eb", fontStyle: "italic", marginTop: "2px", paddingLeft: "2px" }}>
                  aka "{currentNickname}"
                </div>
              )}
            </div>
            {exercise.name.trim() && (() => {
              const url = exercise.videoUrl || getExerciseVideoUrl(exercise.name.trim(), videoGender) || "https://www.precisionnutrition.com/video-exercise-library";
              return (
                <button
                  type="button"
                  onClick={() => window.open(url, "_blank")}
                  title={`Watch ${videoGender === "men" ? "men's" : "women's"} demo video`}
                  style={{ background: "#e0f2fe", border: "1.5px solid #bfdbfe", borderRadius: "8px", padding: "6px 8px", cursor: "pointer", fontSize: "15px", flexShrink: 0, lineHeight: 1 }}
                >🎬</button>
              );
            })()}
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1.5px solid #bfdbfe", borderRadius: "10px", zIndex: 100, boxShadow: "0 4px 12px rgba(30,58,138,0.12)", maxHeight: "200px", overflowY: "auto" }}>
              {suggestions.map((name) => {
                const nick = exerciseNicknames[name];
                const prevSetsForEx = previousSets[name];
                return (
                  <button
                    key={name}
                    onMouseDown={() => selectSuggestion(name)}
                    style={{ display: "block", width: "100%", padding: "10px 12px", background: "none", border: "none", textAlign: "left", color: "#1e3a5f", fontSize: "14px", cursor: "pointer", borderBottom: "1px solid #f0f4f8" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: nick ? "bold" : "normal" }}>{name}</span>
                      {nick && (
                        <span style={{ fontSize: "12px", color: "#2563eb", fontStyle: "italic", background: "#e0f2fe", borderRadius: "6px", padding: "1px 6px" }}>
                          "{nick}"
                        </span>
                      )}
                    </div>
                    {prevSetsForEx?.sets?.length > 0 && (
                      <div style={{ fontSize: "11px", color: "#6b8cb0", marginTop: "2px" }}>
                        prev: {prevSetsForEx.sets.map((s) => `${s.reps}×${s.weight}lbs`).join(", ")}
                        {prevSetsForEx.restSecs && <span style={{ marginLeft: "4px" }}>· {prevSetsForEx.restSecs}s rest</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <button onClick={onRemove} style={{ background: "#fca5a5", border: "none", borderRadius: "8px", padding: "6px 10px", color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: "13px", flexShrink: 0 }}>Remove</button>
      </div>

      {previousSets[exercise.name]?.sets?.length > 0 && (
        <div style={{ fontSize: "11px", color: "#6b8cb0", marginBottom: "8px", fontStyle: "italic" }}>
          Last time: {previousSets[exercise.name].sets.map((s, i) => `Set ${i + 1}: ${s.reps}×${s.weight}lbs`).join(" · ")}
          {previousSets[exercise.name].restSecs && <span style={{ marginLeft: "6px" }}>· {previousSets[exercise.name].restSecs}s rest</span>}
        </div>
      )}

      <div style={{ fontSize: "11px", color: "#6b8cb0", marginBottom: "6px", fontWeight: "bold" }}>SETS (reps × weight)</div>
      {exercise.sets.map((set, i) => (
        <SetRow key={i} set={set} idx={i} onChange={(u) => updateSet(i, u)} onRemove={() => removeSet(i)} />
      ))}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
        <button onClick={addSet} style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "6px 12px", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>+ Add Set</button>
      </div>
      <ExerciseRestTimer
        globalTimer={globalTimer}
        defaultRestSecs={exercise.restSecs || 60}
        onRestSecsChange={(secs) => onChange({ ...exercise, restSecs: secs })}
      />
    </div>
  );
}

// ─── Exercises Tab ────────────────────────────────────────────────────────────
function ExercisesTab({ userId, videoGender }) {
  const [search, setSearch] = useState("");
  const [nicknames, setNicknames] = useState({}); // { [officialName]: nickname }
  const [editingNickname, setEditingNickname] = useState(null); // officialName being edited
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [savingNickname, setSavingNickname] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Load nicknames from Firestore
  useEffect(() => {
    if (!userId) return;
    getDocs(collection(db, "users", userId, "exerciseNicknames"))
      .then((snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          // Key by officialName field if present, otherwise fall back to doc id
          const data = d.data();
          const key = data.officialName || d.id.replace(/_/g, "/");
          map[key] = data.nickname || "";
        });
        setNicknames(map);
      })
      .catch(console.error);
  }, [userId]);

  async function saveNickname(officialName, nickname) {
    if (!userId) return;
    setSavingNickname(true);
    try {
      // Firestore doc IDs can't contain "/" — replace with a safe separator
      const docId = officialName.replace(/[^a-zA-Z0-9 _-]/g, "_");
      if (nickname.trim()) {
        await setDoc(doc(db, "users", userId, "exerciseNicknames", docId), {
          nickname: nickname.trim(),
          officialName,
        });
        setNicknames((prev) => ({ ...prev, [officialName]: nickname.trim() }));
      } else {
        await deleteDoc(doc(db, "users", userId, "exerciseNicknames", docId));
        setNicknames((prev) => { const n = { ...prev }; delete n[officialName]; return n; });
      }
      setEditingNickname(null);
      setNicknameDraft("");
    } catch (e) {
      console.error("saveNickname error:", e);
    } finally {
      setSavingNickname(false);
    }
  }

  // Group exercises by category, sorted alphabetically within each group
  // The count shown is the true library size
  const allExercises = [...exerciseLibrary].sort((a, b) => a.name.localeCompare(b.name));

  const filtered = search.trim()
    ? allExercises.filter((ex) => {
        const q = search.toLowerCase();
        return (
          ex.name.toLowerCase().includes(q) ||
          (nicknames[ex.name] || "").toLowerCase().includes(q) ||
          ex.category.toLowerCase().includes(q)
        );
      })
    : allExercises;

  // Group by category
  const byCategory = {};
  filtered.forEach((ex) => {
    if (!byCategory[ex.category]) byCategory[ex.category] = [];
    byCategory[ex.category].push(ex);
  });
  const categories = Object.keys(byCategory).sort();

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "12px",
    border: "1.5px solid #bfdbfe",
    background: "#f5faff",
    color: "#1e3a5f",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  return (
    <div>
      <p style={{ color: "#6b8cb0", fontSize: "13px", marginTop: 0, marginBottom: "14px" }}>
        Browse all {allExercises.length} exercises. Tap ✏️ to add a personal nickname — it'll appear in your workout logs.
      </p>

      {/* Search */}
      <input
        type="text"
        placeholder="Search exercises…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ ...inputStyle, marginBottom: "14px" }}
        autoComplete="off"
      />

      {filtered.length === 0 && (
        <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", padding: "20px 0" }}>
          No exercises match "{search}"
        </div>
      )}

      {/* Category groups */}
      {categories.map((cat) => {
        const isOpen = search.trim() ? true : expandedCategory === cat;
        return (
          <div key={cat} style={{ marginBottom: "10px" }}>
            {/* Category header */}
            <button
              type="button"
              onClick={() => setExpandedCategory(isOpen && !search.trim() ? null : cat)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 14px",
                borderRadius: "12px",
                border: "2px solid #bfdbfe",
                background: isOpen ? "#e0f2fe" : "#f5faff",
                color: "#1e3a5f",
                fontWeight: "bold",
                fontSize: "14px",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span>{cat} <span style={{ color: "#6b8cb0", fontWeight: "normal", fontSize: "12px" }}>({byCategory[cat].length})</span></span>
              <span style={{ fontSize: "12px", color: "#6b8cb0" }}>{isOpen ? "▲" : "▼"}</span>
            </button>

            {/* Exercise list */}
            {isOpen && (
              <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {byCategory[cat].map((ex) => {
                  const nickname = nicknames[ex.name] || "";
                  const isEditing = editingNickname === ex.name;
                  const videoUrl = getExerciseVideoUrl(ex.name, videoGender);

                  return (
                    <div
                      key={ex.name}
                      style={{
                        background: "#f8fbff",
                        borderRadius: "12px",
                        padding: "10px 14px",
                        border: "1.5px solid #bfdbfe",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", lineHeight: 1.3 }}>
                            {ex.name}
                          </div>
                          {nickname && !isEditing && (
                            <div style={{ color: "#2563eb", fontSize: "12px", marginTop: "2px", fontStyle: "italic" }}>
                              "{nickname}"
                            </div>
                          )}
                        </div>

                        {/* Video button */}
                        {videoUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(videoUrl, "_blank")}
                            title="Watch demo video"
                            style={{ background: "#e0f2fe", border: "1.5px solid #bfdbfe", borderRadius: "8px", padding: "5px 8px", cursor: "pointer", fontSize: "14px", flexShrink: 0, lineHeight: 1 }}
                          >🎬</button>
                        )}

                        {/* Nickname edit button */}
                        {!isEditing && (
                          <button
                            type="button"
                            onClick={() => { setEditingNickname(ex.name); setNicknameDraft(nickname); }}
                            title={nickname ? "Edit nickname" : "Add nickname"}
                            style={{ background: nickname ? "#e0f2fe" : "#f5faff", border: "1.5px solid #bfdbfe", borderRadius: "8px", padding: "5px 8px", cursor: "pointer", fontSize: "13px", flexShrink: 0, color: "#4b6b8a" }}
                          >✏️</button>
                        )}
                      </div>

                      {/* Inline nickname editor */}
                      {isEditing && (
                        <div style={{ marginTop: "10px" }}>
                          <div style={{ fontSize: "11px", color: "#6b8cb0", marginBottom: "4px", fontWeight: "bold" }}>
                            NICKNAME (leave blank to remove)
                          </div>
                          <input
                            type="text"
                            placeholder={`e.g. "Chest Press"`}
                            value={nicknameDraft}
                            onChange={(e) => setNicknameDraft(e.target.value)}
                            autoFocus
                            style={{ ...inputStyle, marginBottom: "8px" }}
                          />
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              type="button"
                              onClick={() => saveNickname(ex.name, nicknameDraft)}
                              disabled={savingNickname}
                              style={{ flex: 1, padding: "8px", borderRadius: "10px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                            >{savingNickname ? "Saving…" : "Save"}</button>
                            <button
                              type="button"
                              onClick={() => { setEditingNickname(null); setNicknameDraft(""); }}
                              style={{ flex: 1, padding: "8px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                            >Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function localDateKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// ─── Equipment-based exercise auto-swap ───────────────────────────────────────
function buildSets(ex) {
  return Array.from({ length: ex.sets || 3 }, () => ({ reps: ex.reps || "", weight: "" }));
}

function resolveWorkoutForEquipment(workout, availableEquipment) {
  const has = (required) => (required || []).every((k) => availableEquipment.includes(k));
  return {
    name: workout.name,
    exercises: workout.exercises.map((ex) => {
      if (has(ex.equipment)) {
        return { name: ex.primaryName, sets: buildSets(ex), restSecs: ex.restSecs, videoUrl: ex.videoUrl || null };
      }
      for (const alt of ex.alternatives || []) {
        if (has(alt.equipment)) {
          return { name: alt.name, sets: buildSets(ex), restSecs: ex.restSecs, _swappedFrom: ex.primaryName, videoUrl: ex.videoUrl || null };
        }
      }
      const last = ex.alternatives?.at(-1);
      return { name: last?.name || ex.primaryName, sets: buildSets(ex), restSecs: ex.restSecs, _swappedFrom: last?.name !== ex.primaryName ? ex.primaryName : undefined, videoUrl: ex.videoUrl || null };
    }),
  };
}

// ─── Main WorkoutsScreen ──────────────────────────────────────────────────────
export default function WorkoutsScreen({
  userId,
  programTag = "",
  videoGender = "women",
  onVideoGenderChange,
  onWorkoutComplete,
  onWorkoutDeleted,
  workoutAutoSideQuest = "",
  onWorkoutAutoSideQuestChange,
  activeSideQuests = [],
  scheduleStartWorkout = null,
  onScheduleStartConsumed,
}) {
  const [tab, setTab] = useState("ondemand");
  const [onDemandWorkouts, setOnDemandWorkouts] = useState([]);
  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLogConfirm, setDeleteLogConfirm] = useState(null);

  // Settings panel
  const [showSettings, setShowSettings] = useState(false);

  // Schedule
  const [scheduledWorkouts, setScheduledWorkouts] = useState([]);
  const [showScheduleSheet, setShowScheduleSheet] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleWorkoutType, setScheduleWorkoutType] = useState("blank");
  const [scheduleWorkoutId, setScheduleWorkoutId] = useState("");
  const [scheduleRecurring, setScheduleRecurring] = useState(false);
  const [scheduleDays, setScheduleDays] = useState([]); // days of week [0-6], sun=0
  const [scheduleEndType, setScheduleEndType] = useState("never"); // "never" | "weeks" | "date"
  const [scheduleWeeks, setScheduleWeeks] = useState(6);
  const [scheduleEndDate, setScheduleEndDate] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleDeleteConfirm, setScheduleDeleteConfirm] = useState(null);
  const [scheduleMoveModal, setScheduleMoveModal] = useState(null);
  const [scheduleMoveDate, setScheduleMoveDate] = useState("");

  // All exercise names the user has ever done (for autocomplete)
  const [allExerciseNames, setAllExerciseNames] = useState([]);
  // Most recent sets per exercise name (for pre-fill)
  const [previousSets, setPreviousSets] = useState({});
  // Nickname map: { [officialName]: nickname }
  const [exerciseNicknames, setExerciseNicknames] = useState({});

  // Active workout session
  const [workoutName, setWorkoutName] = useState("");
  const [exercises, setExercises] = useState([]);
  const [savingWorkout, setSavingWorkout] = useState(false);
  const [saveWorkoutName, setSaveWorkoutName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [workoutStartedAt, setWorkoutStartedAt] = useState(null);
  const globalTimer = useRestTimer();

  // Programs tab state
  const [programs, setPrograms] = useState([]);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [workoutEquipmentHistory, setWorkoutEquipmentHistory] = useState({});
  const [expandedProgramId, setExpandedProgramId] = useState(null);
  const [equipmentOverride, setEquipmentOverride] = useState({});
  const [showEquipmentPickerFor, setShowEquipmentPickerFor] = useState(null);

  useEffect(() => {
    if (!userId) return;
    loadAll();
    loadSchedule();
  }, [userId]);

  // If a scheduled workout was clicked from the dashboard, auto-start it
  useEffect(() => {
    if (!scheduleStartWorkout) return;
    if (scheduleStartWorkout.workoutType === "blank") {
      startBlankWorkout();
    } else if (scheduleStartWorkout.template) {
      startWorkout(scheduleStartWorkout.template);
    }
    if (onScheduleStartConsumed) onScheduleStartConsumed();
  }, [scheduleStartWorkout]);

  async function loadAll() {
    setLoading(true);
    try {
      const odSnap = await getDocs(query(collection(db, "onDemandWorkouts"), orderBy("createdAt", "desc")));
      setOnDemandWorkouts(odSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const swSnap = await getDocs(query(collection(db, "users", userId, "savedWorkouts"), orderBy("createdAt", "desc")));
      setSavedWorkouts(swSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const logSnap = await getDocs(query(collection(db, "users", userId, "workoutLogs"), orderBy("completedAt", "desc")));
      const logs = logSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setWorkoutLogs(logs);

      // Build exercise name list: merge PN library + user history (newest wins for prevSets)
      const nameSet = new Set(libraryNames);
      const prevSets = {};
      [...logs].reverse().forEach((log) => {
        (log.exercises || []).forEach((ex) => {
          if (ex.name) {
            nameSet.add(ex.name);
            prevSets[ex.name] = {
              sets: (ex.sets || []).filter((s) => s.reps || s.weight),
              restSecs: ex.restSecs || null,
            };
          }
        });
      });
      setAllExerciseNames([...nameSet].sort());
      setPreviousSets(prevSets);

      // Load exercise nicknames
      try {
        const nickSnap = await getDocs(collection(db, "users", userId, "exerciseNicknames"));
        const nickMap = {};
        nickSnap.docs.forEach((d) => {
          const data = d.data();
          const key = data.officialName || d.id;
          if (data.nickname) nickMap[key] = data.nickname;
        });
        setExerciseNicknames(nickMap);
      } catch (e) {
        console.error("Failed to load nicknames:", e);
      }

      // Load workout programs + equipment prefs from user doc
      try {
        const progs = await getWorkoutPrograms(programTag, userId);
        setPrograms(progs);
        const userSnap = await getDoc(doc(db, "users", userId));
        const userDocData = userSnap.exists() ? userSnap.data() : {};
        setAvailableEquipment(userDocData.availableEquipment || []);
        setWorkoutEquipmentHistory(userDocData.workoutEquipmentHistory || {});
      } catch (e) {
        console.error("Failed to load programs/equipment:", e);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadSchedule() {
    if (!userId) return;
    const today = localDateKey();
    const lookahead = new Date();
    lookahead.setDate(lookahead.getDate() + 28);
    const lookaheadKey = localDateKey(lookahead);

    const snap = await getDocs(collection(db, "users", userId, "workoutSchedule"));
    const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const oneOffs = all.filter((d) => !d.recurring && d.dateKey >= today && d.dateKey <= lookaheadKey);
    const rules = all.filter((d) => d.recurring);

    const expanded = [];
    rules.forEach((rule) => {
      if (!rule.days || rule.days.length === 0) return;
      const startKey = rule.startDate || today;
      const endKey = rule.endDate || null;
      for (let i = 0; i <= 28; i++) {
        const d = new Date();
        d.setDate(d.getDate() + i);
        const dk = localDateKey(d);
        if (dk < startKey) continue;
        if (endKey && dk > endKey) continue;
        if (rule.days.includes(d.getDay())) {
          expanded.push({ ...rule, dateKey: dk, ruleId: rule.id, isRuleOccurrence: true });
        }
      }
    });

    setScheduledWorkouts([...oneOffs, ...expanded].sort((a, b) => a.dateKey.localeCompare(b.dateKey)));
  }

  function randomId() {
    return Math.random().toString(36).slice(2, 10);
  }

  function resetAddForm() {
    const today = localDateKey();
    setScheduleDate(today);
    setScheduleWorkoutType("blank");
    setScheduleWorkoutId("");
    setScheduleRecurring(false);
    setScheduleDays([]);
    setScheduleEndType("weeks");
    setScheduleWeeks(6);
    setScheduleEndDate("");
    setShowAddForm(false);
  }

  async function saveScheduleEntry() {
    if (!scheduleDate || !userId) return;
    setScheduleSaving(true);
    try {
      const name = scheduleWorkoutType === "blank"
        ? "New Workout"
        : scheduleWorkoutType === "saved"
          ? savedWorkouts.find((w) => w.id === scheduleWorkoutId)?.name || "Workout"
          : onDemandWorkouts.find((w) => w.id === scheduleWorkoutId)?.name || "Workout";

      if (scheduleRecurring && scheduleDays.length > 0) {
        // Store a single rule doc — expanded on load
        let endDate = null;
        if (scheduleEndType === "date" && scheduleEndDate) endDate = scheduleEndDate;
        else if (scheduleEndType === "weeks") {
          const d = new Date(scheduleDate + "T12:00:00");
          d.setDate(d.getDate() + (scheduleWeeks || 6) * 7);
          endDate = localDateKey(d);
        }
        await addDoc(collection(db, "users", userId, "workoutSchedule"), {
          workoutName: name,
          workoutType: scheduleWorkoutType,
          workoutId: scheduleWorkoutId || null,
          recurring: true,
          days: scheduleDays,
          startDate: scheduleDate,
          endDate,
          createdAt: serverTimestamp(),
        });
      } else {
        // Single one-off entry
        await addDoc(collection(db, "users", userId, "workoutSchedule"), {
          workoutName: name,
          workoutType: scheduleWorkoutType,
          workoutId: scheduleWorkoutId || null,
          recurring: false,
          dateKey: scheduleDate,
          createdAt: serverTimestamp(),
        });
      }

      await loadSchedule();
      resetAddForm();
    } finally {
      setScheduleSaving(false);
    }
  }

  async function deleteScheduleEntry(item, deleteRule = false) {
    if (!userId) return;
    if (item.isRuleOccurrence && deleteRule) {
      await deleteDoc(doc(db, "users", userId, "workoutSchedule", item.ruleId));
    } else if (item.isRuleOccurrence && !deleteRule) {
      // End the rule the day before this occurrence
      const yesterday = new Date(item.dateKey + "T12:00:00");
      yesterday.setDate(yesterday.getDate() - 1);
      await updateDoc(doc(db, "users", userId, "workoutSchedule", item.ruleId), {
        endDate: localDateKey(yesterday),
      });
      // Create a continuation rule starting the day after this occurrence
      const dayAfter = new Date(item.dateKey + "T12:00:00");
      dayAfter.setDate(dayAfter.getDate() + 1);
      await addDoc(collection(db, "users", userId, "workoutSchedule"), {
        workoutName: item.workoutName,
        workoutType: item.workoutType,
        workoutId: item.workoutId || null,
        recurring: true,
        days: item.days,
        startDate: localDateKey(dayAfter),
        endDate: item.endDate || null,
        createdAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(doc(db, "users", userId, "workoutSchedule", item.id));
    }
    setScheduleDeleteConfirm(null);
    await loadSchedule();
  }

  async function moveScheduleEntry(item, newDate, moveAll = false) {
    if (!userId) return;
    if (item.isRuleOccurrence && moveAll) {
      // Update the rule: replace the moved day with the new day of week, update startDate
      const newDow = new Date(newDate + "T12:00:00").getDay();
      const oldDow = new Date(item.dateKey + "T12:00:00").getDay();
      const newDays = (item.days || []).map((d) => d === oldDow ? newDow : d);
      await updateDoc(doc(db, "users", userId, "workoutSchedule", item.ruleId), {
        days: [...new Set(newDays)],
        startDate: newDate,
      });
    } else {
      // Move just this occurrence: end the rule at yesterday, create a new one-off for newDate
      if (item.isRuleOccurrence) {
        const yesterday = new Date(item.dateKey + "T12:00:00");
        yesterday.setDate(yesterday.getDate() - 1);
        await updateDoc(doc(db, "users", userId, "workoutSchedule", item.ruleId), {
          endDate: localDateKey(yesterday),
        });
        await addDoc(collection(db, "users", userId, "workoutSchedule"), {
          workoutName: item.workoutName,
          workoutType: item.workoutType,
          workoutId: item.workoutId || null,
          recurring: false,
          dateKey: newDate,
          createdAt: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, "users", userId, "workoutSchedule", item.id), { dateKey: newDate });
      }
    }
    setScheduleMoveModal(null);
    setScheduleMoveDate("");
    await loadSchedule();
  }

  function startWorkout(template) {
    setWorkoutName(template?.name || "");
    setExercises(
      (template?.exercises || []).map((ex) => ({
        name: ex.name || "",
        sets: (ex.sets || [{ reps: "", weight: "" }]).map((s) => ({ reps: s.reps || "", weight: s.weight || "" })),
        restSecs: ex.restSecs || previousSets[ex.name]?.restSecs || 60,
      }))
    );
    setWorkoutStartedAt(Date.now());
    setTab("active");
  }

  function startBlankWorkout() {
    setWorkoutName("");
    setExercises([{ name: "", sets: [{ reps: "", weight: "" }], restSecs: 60 }]);
    setWorkoutStartedAt(Date.now());
    setTab("active");
  }

  function addExercise() {
    setExercises((prev) => [...prev, { name: "", sets: [{ reps: "", weight: "" }], restSecs: 60 }]);
  }

  function updateExercise(i, updated) {
    setExercises((prev) => prev.map((ex, idx) => idx === i ? updated : ex));
  }

  function removeExercise(i) {
    setExercises((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function finishWorkout() {
    if (!userId) return;
    setSavingWorkout(true);
    try {
      await addDoc(collection(db, "users", userId, "workoutLogs"), {
        name: workoutName || "Workout",
        exercises: exercises.map((ex) => ({
          name: ex.name,
          sets: ex.sets.filter((s) => s.weight || s.reps),
          restSecs: ex.restSecs || 60,
        })),
        completedAt: serverTimestamp(),
      });
      const todayKey = localDateKey();
      recordAutoProgress(userId, "workout_logged", todayKey);
      if (onWorkoutComplete) onWorkoutComplete();
      await loadAll();
      setTab("history");
    } catch (e) {
      console.error(e);
    } finally {
      setSavingWorkout(false);
    }
  }

  async function saveAsCustomWorkout(name, exs) {
    if (!userId || !name.trim()) return;
    setSavingWorkout(true);
    try {
      await addDoc(collection(db, "users", userId, "savedWorkouts"), {
        name: name.trim(),
        exercises: (exs || exercises).map((ex) => ({
          name: ex.name,
          sets: ex.sets.map((s) => ({ reps: s.reps, weight: s.weight })),
        })),
        createdAt: serverTimestamp(),
      });
      await loadAll();
      setShowSaveDialog(false);
      setSaveWorkoutName("");
    } catch (e) {
      console.error(e);
    } finally {
      setSavingWorkout(false);
    }
  }

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  async function deleteCustomWorkout(id) {
    if (!userId) return;
    try {
      await deleteDoc(doc(db, "users", userId, "savedWorkouts", id));
      setConfirmDeleteId(null);
      await loadAll();
    } catch (e) {
      console.error(e);
    }
  }

  async function deleteWorkoutLog(id) {
    if (!userId) return;
    try {
      const log = workoutLogs.find((l) => l.id === id);
      await deleteDoc(doc(db, "users", userId, "workoutLogs", id));
      setWorkoutLogs((prev) => prev.filter((l) => l.id !== id));
      setDeleteLogConfirm(null);
      if (log?.completedAt) {
        const logDate = log.completedAt.toDate ? log.completedAt.toDate() : new Date(log.completedAt);
        const dateKey = localDateKey(logDate);
        removeAutoProgress(userId, "workout_logged", dateKey);
        if (onWorkoutDeleted) onWorkoutDeleted(dateKey);
      }
    } catch (e) {
      console.error(e);
    }
  }

  // Save from history
  const [saveFromHistoryLog, setSaveFromHistoryLog] = useState(null);
  const [saveFromHistoryName, setSaveFromHistoryName] = useState("");

  const card = { background: "#f8fbff", borderRadius: "18px", padding: "18px", border: "2px solid #bfdbfe", marginBottom: "14px" };

  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      style={{ flex: 1, padding: "8px 4px", borderRadius: "10px", border: tab === key ? "2px solid #2563eb" : "2px solid #bfdbfe", background: tab === key ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
    >
      {label}
    </button>
  );

  function getEffectiveEquipment(workoutId) {
    if (workoutId in equipmentOverride) return equipmentOverride[workoutId];
    if (workoutId in workoutEquipmentHistory) return workoutEquipmentHistory[workoutId];
    return availableEquipment;
  }

  async function toggleEquipment(key) {
    const next = availableEquipment.includes(key)
      ? availableEquipment.filter((k) => k !== key)
      : [...availableEquipment, key];
    setAvailableEquipment(next);
    await setDoc(doc(db, "users", userId), { availableEquipment: next }, { merge: true });
  }

  async function startProgramWorkout(workout) {
    const eq = getEffectiveEquipment(workout.id);
    const newHistory = { ...workoutEquipmentHistory, [workout.id]: eq };
    setWorkoutEquipmentHistory(newHistory);
    await setDoc(doc(db, "users", userId), { workoutEquipmentHistory: newHistory }, { merge: true });
    startWorkout(resolveWorkoutForEquipment(workout, eq));
  }

  return (
    <div style={{ paddingBottom: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <h2 style={{ color: "#1e3a5f", fontSize: "22px", margin: 0 }}>💪 Workouts</h2>
        {tab !== "active" && (
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => { setShowScheduleSheet(true); resetAddForm(); }}
              style={{ background: showScheduleSheet ? "#e0f2fe" : "transparent", border: `1px solid ${showScheduleSheet ? "#2563eb" : "#bfdbfe"}`, borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: showScheduleSheet ? "#2563eb" : "#6b7280", fontSize: "16px" }}
              title="Workout Schedule">
              📅
            </button>
            <button
              onClick={() => setShowSettings((v) => !v)}
              style={{ background: showSettings ? "#e0f2fe" : "transparent", border: `1px solid ${showSettings ? "#2563eb" : "#bfdbfe"}`, borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: showSettings ? "#2563eb" : "#6b7280", fontSize: "16px" }}>
              ⚙️
            </button>
          </div>
        )}
      </div>

      {/* Settings panel */}
      {showSettings && tab !== "active" && (
        <div style={{ background: "#f0f9ff", borderRadius: "16px", padding: "16px", border: "1.5px solid #bfdbfe", marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "12px" }}>Workout Settings</div>

          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "12px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "6px" }}>🏋️ My Equipment</div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>Select what you have access to. Workout programs will auto-swap exercises to match.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {EQUIPMENT_LIST.map((eq) => {
                const on = availableEquipment.includes(eq.key);
                return (
                  <button key={eq.key} type="button" onClick={() => toggleEquipment(eq.key)}
                    style={{ padding: "5px 12px", borderRadius: "20px", border: on ? "2px solid #2563eb" : "1.5px solid #bfdbfe", background: on ? "#dbeafe" : "#f5faff", color: on ? "#1d4ed8" : "#4b6b8a", fontSize: "12px", fontWeight: on ? "bold" : "normal", cursor: "pointer" }}>
                    {eq.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "12px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "6px" }}>🎬 Exercise Videos</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {[{ value: "women", label: "👩 Women" }, { value: "men", label: "👨 Men" }].map(({ value, label }) => (
                <button key={value} onClick={async () => { if (onVideoGenderChange) onVideoGenderChange(value); await saveBodyStats(userId, { videoGender: value }); }}
                  style={{ flex: 1, padding: "9px 8px", borderRadius: "12px", border: videoGender === value ? "2px solid #2563eb" : "2px solid #bfdbfe", background: videoGender === value ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: videoGender === value ? "bold" : "normal", cursor: "pointer", fontSize: "13px" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "6px" }}>🏃 Auto-complete side quest on workout</div>
            <select value={workoutAutoSideQuest} onChange={(e) => { if (onWorkoutAutoSideQuestChange) onWorkoutAutoSideQuestChange(e.target.value); }}
              style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "13px" }}>
              <option value="">None (don't auto-complete)</option>
              {activeSideQuests.map((q) => (
                <option key={q.title} value={q.title}>{q.emoji ? `${q.emoji} ` : ""}{q.title}</option>
              ))}
            </select>
            {workoutAutoSideQuest && (
              <div style={{ marginTop: "6px", color: "#16a34a", fontSize: "12px" }}>✓ Finishing a workout will auto-complete "{workoutAutoSideQuest}"</div>
            )}
          </div>
        </div>
      )}

      {tab !== "active" && (
        <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
          {programs.length > 0 && tabBtn("programs", "Programs")}
          {tabBtn("ondemand", "On Demand")}
          {tabBtn("custom", "My Workouts")}
          {tabBtn("history", "History")}
          {tabBtn("exercises", "Exercises")}
        </div>
      )}

      {/* ── Programs ── */}
      {tab === "programs" && (
        <div>
          {availableEquipment.length === 0 && Object.keys(workoutEquipmentHistory).length === 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "12px", padding: "12px 14px", fontSize: "13px", color: "#92400e", marginBottom: "14px" }}>
              ⚙️ Tap the settings icon above to set your available equipment — workouts will auto-customize to match what you have.
            </div>
          )}
          {programs.map((program) => (
            <div key={program.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                <div>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>{program.name}</div>
                  {program.description && <div style={{ color: "#4b6b8a", fontSize: "13px", marginTop: "2px" }}>{program.description}</div>}
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", background: "#eff6ff", color: "#2563eb", fontWeight: "bold" }}>{program.level}</span>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", background: "#f0fdf4", color: "#16a34a" }}>{(program.workouts || []).length} workout days</span>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedProgramId(expandedProgramId === program.id ? null : program.id)}
                  style={{ padding: "6px 14px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                  {expandedProgramId === program.id ? "Hide" : "View Workouts"}
                </button>
              </div>

              {expandedProgramId === program.id && (program.workouts || []).map((workout) => {
                const effectiveEq = getEffectiveEquipment(workout.id);
                const resolved = resolveWorkoutForEquipment(workout, effectiveEq);
                const hasHistory = workout.id in workoutEquipmentHistory;
                const isOverridden = workout.id in equipmentOverride;
                return (
                  <div key={workout.id} style={{ border: "1.5px solid #dbeafe", borderRadius: "14px", padding: "14px", marginTop: "10px", background: "#f0f9ff" }}>
                    {/* Equipment bar */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "12px", color: "#4b6b8a", fontWeight: "bold" }}>Equipment:</div>
                      {effectiveEq.length === 0 ? (
                        <span style={{ fontSize: "12px", color: "#9ca3af" }}>Bodyweight only</span>
                      ) : (
                        effectiveEq.map((k) => {
                          const eq = EQUIPMENT_LIST.find((e) => e.key === k);
                          return eq ? (
                            <span key={k} style={{ padding: "2px 8px", borderRadius: "10px", background: "#dbeafe", color: "#1d4ed8", fontSize: "11px", fontWeight: "bold" }}>{eq.label}</span>
                          ) : null;
                        })
                      )}
                      {(hasHistory || isOverridden) && <span style={{ fontSize: "11px", color: "#6b7280" }}>· custom</span>}
                      <button onClick={() => setShowEquipmentPickerFor(workout.id)}
                        style={{ padding: "3px 10px", borderRadius: "8px", border: "1px solid #bfdbfe", background: "#fff", color: "#2563eb", fontSize: "12px", cursor: "pointer" }}>
                        ⚙️ Change
                      </button>
                    </div>

                    <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "8px" }}>{resolved.name}</div>
                    {resolved.exercises.map((ex, i) => (
                      <div key={i} style={{ marginBottom: "6px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px" }}>{ex.name}</span>
                          <span style={{ padding: "2px 8px", borderRadius: "8px", background: "#e0f2fe", color: "#0369a1", fontSize: "11px" }}>{ex.sets?.length}×{ex.sets?.[0]?.reps}</span>
                        </div>
                        {ex._swappedFrom && (
                          <div style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "2px" }}>↔ was: {ex._swappedFrom}</div>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => startProgramWorkout(workout)}
                      style={{ width: "100%", marginTop: "10px", padding: "12px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>
                      Start Workout
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Equipment picker bottom sheet */}
      {showEquipmentPickerFor !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "flex-end" }}
          onClick={() => setShowEquipmentPickerFor(null)}>
          <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "20px", width: "100%", maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "6px" }}>Equipment for this workout</div>
            <div style={{ fontSize: "13px", color: "#4b6b8a", marginBottom: "14px" }}>Select what's available. The exercise list will update live.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
              {EQUIPMENT_LIST.map((eq) => {
                const current = getEffectiveEquipment(showEquipmentPickerFor);
                const on = current.includes(eq.key);
                return (
                  <button key={eq.key} type="button"
                    onClick={() => {
                      const current2 = getEffectiveEquipment(showEquipmentPickerFor);
                      const next = current2.includes(eq.key)
                        ? current2.filter((k) => k !== eq.key)
                        : [...current2, eq.key];
                      setEquipmentOverride((prev) => ({ ...prev, [showEquipmentPickerFor]: next }));
                    }}
                    style={{ padding: "7px 14px", borderRadius: "20px", border: on ? "2px solid #2563eb" : "1.5px solid #bfdbfe", background: on ? "#dbeafe" : "#f5faff", color: on ? "#1d4ed8" : "#4b6b8a", fontSize: "13px", fontWeight: on ? "bold" : "normal", cursor: "pointer" }}>
                    {eq.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
                onClick={() => setShowEquipmentPickerFor(null)}>
                Use this equipment
              </button>
              <button style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
                onClick={() => {
                  setEquipmentOverride((prev) => {
                    const next = { ...prev };
                    delete next[showEquipmentPickerFor];
                    return next;
                  });
                  setShowEquipmentPickerFor(null);
                }}>
                Reset to defaults
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── On Demand ── */}
      {tab === "ondemand" && (
        <div>
          <button onClick={startBlankWorkout} style={{ width: "100%", padding: "14px", borderRadius: "16px", border: "2px dashed #93c5fd", background: "#f0f9ff", color: "#1e3a5f", fontWeight: "bold", fontSize: "15px", cursor: "pointer", marginBottom: "16px" }}>
            + Start a New Workout
          </button>
          {loading ? <div style={{ color: "#6b8cb0", fontSize: "13px" }}>Loading…</div>
            : onDemandWorkouts.length === 0 ? (
              <div style={{ ...card, textAlign: "center", color: "#6b8cb0", fontSize: "13px" }}>No on-demand workouts uploaded yet. Check back soon!</div>
            ) : onDemandWorkouts.map((w) => (
              <div key={w.id} style={card}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "4px" }}>{w.name}</div>
                {w.description && <div style={{ color: "#4b6b8a", fontSize: "13px", marginBottom: "10px" }}>{w.description}</div>}
                {w.duration && <div style={{ fontSize: "12px", color: "#6b8cb0", marginBottom: "10px" }}>⏱ {w.duration}</div>}
                <button onClick={() => startWorkout(w)} style={{ padding: "10px 20px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>Start Workout</button>
              </div>
            ))}
        </div>
      )}

      {/* ── My Workouts ── */}
      {tab === "custom" && (
        <div>
          <button onClick={startBlankWorkout} style={{ width: "100%", padding: "14px", borderRadius: "16px", border: "2px dashed #93c5fd", background: "#f0f9ff", color: "#1e3a5f", fontWeight: "bold", fontSize: "15px", cursor: "pointer", marginBottom: "16px" }}>
            + Build a New Workout
          </button>
          {loading ? <div style={{ color: "#6b8cb0", fontSize: "13px" }}>Loading…</div>
            : savedWorkouts.length === 0 ? (
              <div style={{ ...card, textAlign: "center", color: "#6b8cb0", fontSize: "13px" }}>No saved workouts yet. Build one and save it to reuse it!</div>
            ) : savedWorkouts.map((w) => (
              <div key={w.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>{w.name}</div>
                  {confirmDeleteId === w.id ? (
                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: "bold" }}>Delete?</span>
                      <button onClick={() => deleteCustomWorkout(w.id)} style={{ padding: "4px 10px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Yes</button>
                      <button onClick={() => setConfirmDeleteId(null)} style={{ padding: "4px 10px", borderRadius: "8px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>No</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDeleteId(w.id)} style={{ background: "none", border: "none", color: "#f87171", fontSize: "18px", cursor: "pointer", padding: "0" }}>🗑</button>
                  )}
                </div>
                <div style={{ fontSize: "12px", color: "#6b8cb0", marginBottom: "10px" }}>{(w.exercises || []).map((e) => e.name).filter(Boolean).join(" · ")}</div>
                <button onClick={() => startWorkout(w)} style={{ padding: "10px 20px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>Start Workout</button>
              </div>
            ))}
        </div>
      )}

      {/* ── Active Workout ── */}
      {tab === "active" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <button onClick={() => setShowLeaveConfirm(true)} style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "8px 12px", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", fontSize: "13px" }}>← Back</button>
            <input
              type="text"
              placeholder="Workout name"
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              style={{ flex: 1, padding: "10px 12px", borderRadius: "12px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "15px", fontWeight: "bold" }}
            />
          </div>

          {/* Global rest timer + elapsed */}
          <GlobalRestTimer timer={globalTimer} workoutStartedAt={workoutStartedAt || Date.now()} />

          {exercises.map((ex, i) => (
            <ExerciseBlock
              key={i}
              exercise={ex}
              onChange={(u) => updateExercise(i, u)}
              onRemove={() => removeExercise(i)}
              allExerciseNames={allExerciseNames}
              previousSets={previousSets}
              videoGender={videoGender}
              exerciseNicknames={exerciseNicknames}
              globalTimer={globalTimer}
            />
          ))}

          <button onClick={addExercise} style={{ width: "100%", padding: "12px", borderRadius: "14px", border: "2px dashed #93c5fd", background: "#f0f9ff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer", marginBottom: "14px" }}>+ Add Exercise</button>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => { setSaveWorkoutName(workoutName || ""); setShowSaveDialog(true); }}
              style={{ flex: 1, padding: "12px", borderRadius: "14px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
            >💾 Save Workout</button>
            <button
              onClick={finishWorkout}
              disabled={savingWorkout}
              style={{ flex: 1, padding: "12px", borderRadius: "14px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
            >{savingWorkout ? "Saving…" : "✅ Finish & Log"}</button>
          </div>

          {/* Leave workout confirm */}
          {showLeaveConfirm && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
              <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", width: "100%", maxWidth: "340px", boxShadow: "0 8px 32px rgba(30,58,138,0.18)" }}>
                <h3 style={{ margin: "0 0 10px", color: "#1e3a5f", fontSize: "18px" }}>Leave workout?</h3>
                <p style={{ margin: "0 0 18px", color: "#4b6b8a", fontSize: "14px" }}>Your progress will be lost if you go back without finishing or saving.</p>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={() => setShowLeaveConfirm(false)}
                    style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
                  >Keep Going</button>
                  <button
                    onClick={() => { setShowLeaveConfirm(false); setTab("ondemand"); }}
                    style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}
                  >Leave</button>
                </div>
              </div>
            </div>
          )}

          {showSaveDialog && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
              <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", width: "100%", maxWidth: "360px", boxShadow: "0 8px 32px rgba(30,58,138,0.18)" }}>
                <h3 style={{ margin: "0 0 14px", color: "#1e3a5f", fontSize: "18px" }}>Save Workout</h3>
                <input
                  type="text"
                  placeholder="Give this workout a name"
                  value={saveWorkoutName}
                  onChange={(e) => setSaveWorkoutName(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "12px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px", boxSizing: "border-box", marginBottom: "14px" }}
                />
                <div style={{ display: "flex", gap: "10px" }}>
                  <button onClick={() => { setShowSaveDialog(false); setSaveWorkoutName(""); }} style={{ flex: 1, padding: "10px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer" }}>Cancel</button>
                  <button
                    onClick={() => saveAsCustomWorkout(saveWorkoutName)}
                    disabled={savingWorkout || !saveWorkoutName.trim()}
                    style={{ flex: 1, padding: "10px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer" }}
                  >{savingWorkout ? "Saving…" : "Save"}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── History ── */}
      {tab === "history" && (
        <div>
          {loading ? <div style={{ color: "#6b8cb0", fontSize: "13px" }}>Loading…</div>
            : workoutLogs.length === 0 ? (
              <div style={{ ...card, textAlign: "center", color: "#6b8cb0", fontSize: "13px" }}>No workouts logged yet. Finish a workout to see your history here.</div>
            ) : workoutLogs.map((log) => {
              const dateStr = log.completedAt?.toDate
                ? log.completedAt.toDate().toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                : "Unknown date";
              const isExpanded = expandedLog === log.id;
              return (
                <div key={log.id} style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setExpandedLog(isExpanded ? null : log.id)}>
                    <div>
                      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{log.name || "Workout"}</div>
                      <div style={{ fontSize: "12px", color: "#6b8cb0", marginTop: "2px" }}>{dateStr}</div>
                    </div>
                    <span style={{ color: "#6b8cb0", fontSize: "18px" }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: "12px" }}>
                      {(log.exercises || []).map((ex, ei) => (
                        <div key={ei} style={{ marginBottom: "10px" }}>
                          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "4px" }}>{ex.name}</div>
                          {(ex.sets || []).map((s, si) => (
                            <div key={si} style={{ fontSize: "12px", color: "#4b6b8a", marginLeft: "10px" }}>
                              Set {si + 1}: {s.reps ? `${s.reps} reps` : "—"} × {s.weight ? `${s.weight} lbs` : "—"}
                            </div>
                          ))}
                        </div>
                      ))}

                      <div style={{ display: "flex", gap: "10px", marginTop: "10px", alignItems: "center" }}>
                        <button
                          onClick={() => { setSaveFromHistoryLog(log); setSaveFromHistoryName(log.name || ""); }}
                          style={{ padding: "8px 16px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                        >💾 Save as My Workout</button>

                        {deleteLogConfirm === log.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "13px", color: "#991b1b", fontWeight: "bold" }}>Delete?</span>
                            <button onClick={() => deleteWorkoutLog(log.id)} style={{ padding: "6px 12px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Yes</button>
                            <button onClick={() => setDeleteLogConfirm(null)} style={{ padding: "6px 12px", borderRadius: "8px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>No</button>
                          </div>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setDeleteLogConfirm(log.id); }} style={{ padding: "8px 16px", borderRadius: "12px", border: "2px solid #fecaca", background: "#fff5f5", color: "#991b1b", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>🗑 Delete</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}


      {/* ── Exercises ── */}
      {tab === "exercises" && (
        <ExercisesTab userId={userId} videoGender={videoGender} />
      )}

      {/* Save from history dialog */}
      {saveFromHistoryLog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" }}>
          <div style={{ background: "#fff", borderRadius: "20px", padding: "24px", width: "100%", maxWidth: "360px", boxShadow: "0 8px 32px rgba(30,58,138,0.18)" }}>
            <h3 style={{ margin: "0 0 14px", color: "#1e3a5f", fontSize: "18px" }}>Save as My Workout</h3>
            <input
              type="text"
              placeholder="Workout name"
              value={saveFromHistoryName}
              onChange={(e) => setSaveFromHistoryName(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "12px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px", boxSizing: "border-box", marginBottom: "14px" }}
            />
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => { setSaveFromHistoryLog(null); setSaveFromHistoryName(""); }} style={{ flex: 1, padding: "10px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer" }}>Cancel</button>
              <button
                onClick={async () => {
                  await saveAsCustomWorkout(saveFromHistoryName, saveFromHistoryLog.exercises);
                  setSaveFromHistoryLog(null);
                  setSaveFromHistoryName("");
                }}
                disabled={savingWorkout || !saveFromHistoryName.trim()}
                style={{ flex: 1, padding: "10px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer" }}
              >{savingWorkout ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule Sheet Modal ── */}
      {showScheduleSheet && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: "480px", height: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 -8px 40px rgba(30,58,138,0.18)", overflow: "hidden" }}>
            {/* Sheet header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px", borderBottom: "1px solid #e8f0fe", flexShrink: 0 }}>
              <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "17px" }}>📅 Workout Schedule</span>
              <button onClick={() => { setShowScheduleSheet(false); setShowAddForm(false); setScheduleMoveModal(null); }}
                style={{ background: "#f0f4fa", border: "none", borderRadius: "999px", width: "30px", height: "30px", fontSize: "16px", cursor: "pointer", color: "#4b6b8a", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            <div style={{ overflowY: "auto", flex: 1, padding: "16px 20px 24px" }}>

              {/* Add form */}
              {showAddForm ? (
                <div style={{ background: "#f0f9ff", borderRadius: "16px", padding: "16px", marginBottom: "16px", border: "1.5px solid #bfdbfe" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", marginBottom: "14px" }}>New Scheduled Workout</div>

                  {/* Workout picker */}
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ fontSize: "11px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "4px", textTransform: "uppercase" }}>Workout</div>
                    <select value={`${scheduleWorkoutType}::${scheduleWorkoutId}`}
                      onChange={(e) => { const [t, id] = e.target.value.split("::"); setScheduleWorkoutType(t); setScheduleWorkoutId(id || ""); }}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "13px" }}>
                      <option value="blank::">Build a New Workout</option>
                      {savedWorkouts.length > 0 && <optgroup label="My Workouts">{savedWorkouts.map((w) => <option key={w.id} value={`saved::${w.id}`}>{w.name}</option>)}</optgroup>}
                      {onDemandWorkouts.length > 0 && <optgroup label="On Demand">{onDemandWorkouts.map((w) => <option key={w.id} value={`ondemand::${w.id}`}>{w.name}</option>)}</optgroup>}
                    </select>
                  </div>

                  {/* Start date */}
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ fontSize: "11px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "4px", textTransform: "uppercase" }}>Start Date</div>
                    <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                      style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", boxSizing: "border-box" }} />
                  </div>

                  {/* Repeat toggle */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: scheduleRecurring ? "12px" : "16px" }}>
                    <span style={{ fontSize: "13px", color: "#1e3a5f", fontWeight: "bold" }}>🔁 Repeat weekly</span>
                    <button onClick={() => { setScheduleRecurring((v) => !v); setScheduleDays([]); }}
                      style={{ width: "48px", height: "26px", borderRadius: "999px", border: "none", background: scheduleRecurring ? "#2563eb" : "#d1c4e9", cursor: "pointer", position: "relative", flexShrink: 0 }}>
                      <div style={{ position: "absolute", top: "2px", left: scheduleRecurring ? "24px" : "2px", width: "22px", height: "22px", borderRadius: "999px", background: "#fff", transition: "left 0.15s" }} />
                    </button>
                  </div>

                  {scheduleRecurring && (
                    <>
                      {/* Days of week */}
                      <div style={{ marginBottom: "12px" }}>
                        <div style={{ fontSize: "11px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>Days of the week</div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {["Su","M","Tu","W","Th","F","Sa"].map((label, i) => {
                            const on = scheduleDays.includes(i);
                            return (
                              <button key={i} onClick={() => setScheduleDays((prev) => on ? prev.filter((d) => d !== i) : [...prev, i])}
                                style={{ flex: 1, padding: "8px 0", borderRadius: "10px", border: on ? "2px solid #2563eb" : "1.5px solid #bfdbfe", background: on ? "#2563eb" : "#f5faff", color: on ? "#fff" : "#4b6b8a", fontWeight: on ? "bold" : "normal", fontSize: "12px", cursor: "pointer" }}>
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* End condition */}
                      <div style={{ marginBottom: "14px" }}>
                        <div style={{ fontSize: "11px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "6px", textTransform: "uppercase" }}>End</div>
                        <div style={{ display: "flex", gap: "5px", marginBottom: "8px" }}>
                          {[{ v: "never", label: "Never" }, { v: "weeks", label: "After X weeks" }, { v: "date", label: "On a date" }].map(({ v, label }) => (
                            <button key={v} onClick={() => setScheduleEndType(v)}
                              style={{ flex: 1, padding: "7px 4px", borderRadius: "10px", border: scheduleEndType === v ? "2px solid #2563eb" : "1.5px solid #bfdbfe", background: scheduleEndType === v ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: scheduleEndType === v ? "bold" : "normal", fontSize: "11px", cursor: "pointer" }}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {scheduleEndType === "weeks" && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input type="number" min="1" max="52" value={scheduleWeeks}
                              onChange={(e) => setScheduleWeeks(Math.max(1, parseInt(e.target.value) || 1))}
                              style={{ width: "70px", padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", textAlign: "center" }} />
                            <span style={{ fontSize: "13px", color: "#4b6b8a" }}>weeks</span>
                          </div>
                        )}
                        {scheduleEndType === "date" && (
                          <input type="date" value={scheduleEndDate} onChange={(e) => setScheduleEndDate(e.target.value)}
                            style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", boxSizing: "border-box" }} />
                        )}
                        {scheduleEndType === "never" && (
                          <div style={{ fontSize: "12px", color: "#6b8cb0", padding: "6px 0" }}>Repeats every week until you remove it from the schedule.</div>
                        )}
                      </div>
                    </>
                  )}

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={resetAddForm} style={{ flex: 1, padding: "10px", borderRadius: "12px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
                    <button onClick={saveScheduleEntry}
                      disabled={!scheduleDate || scheduleSaving || (scheduleRecurring && scheduleDays.length === 0) || (scheduleRecurring && scheduleEndType === "date" && !scheduleEndDate)}

                      style={{ flex: 2, padding: "10px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: "13px" }}>
                      {scheduleSaving ? "Saving…" : scheduleRecurring ? `Schedule ${scheduleDays.length > 0 ? `(${scheduleDays.length} day${scheduleDays.length > 1 ? "s" : ""}/wk)` : ""}` : "Schedule"}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddForm(true)}
                  style={{ width: "100%", padding: "12px", borderRadius: "14px", border: "2px dashed #93c5fd", background: "#f0f9ff", color: "#1e3a5f", fontWeight: "bold", fontSize: "14px", cursor: "pointer", marginBottom: "16px" }}>
                  + Schedule a Workout
                </button>
              )}

              {/* Move inline form */}
              {scheduleMoveModal && (
                <div style={{ background: "#fff7ed", borderRadius: "14px", padding: "14px", marginBottom: "14px", border: "1.5px solid #fed7aa" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "10px" }}>Move: {scheduleMoveModal.workoutName}</div>
                  <div style={{ fontSize: "11px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "4px", textTransform: "uppercase" }}>Move to</div>
                  <input type="date" value={scheduleMoveDate} onChange={(e) => setScheduleMoveDate(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #fed7aa", background: "#fff", color: "#1e3a5f", fontSize: "14px", boxSizing: "border-box", marginBottom: "10px" }} />
                  <div style={{ display: "grid", gap: "6px" }}>
                    {scheduleMoveModal.recurring && (
                      <button onClick={() => moveScheduleEntry(scheduleMoveModal, scheduleMoveDate, true)} disabled={!scheduleMoveDate}
                        style={{ padding: "9px", borderRadius: "10px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: "12px" }}>
                        Move all future occurrences
                      </button>
                    )}
                    <button onClick={() => moveScheduleEntry(scheduleMoveModal, scheduleMoveDate, false)} disabled={!scheduleMoveDate}
                      style={{ padding: "9px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", fontSize: "12px" }}>
                      {scheduleMoveModal.recurring ? "Move just this one" : "Move"}
                    </button>
                    <button onClick={() => { setScheduleMoveModal(null); setScheduleMoveDate(""); }}
                      style={{ padding: "9px", borderRadius: "10px", border: "1.5px solid #e2e8f0", background: "#f9fafb", color: "#6b7280", fontWeight: "bold", cursor: "pointer", fontSize: "12px" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Upcoming list */}
              {(() => {
                const today = localDateKey();
                const dayKeys = [];
                for (let i = 0; i < 28; i++) {
                  const d = new Date();
                  d.setDate(d.getDate() + i);
                  dayKeys.push(localDateKey(d));
                }
                const hasAny = scheduledWorkouts.length > 0;
                if (!hasAny) return <div style={{ textAlign: "center", color: "#6b8cb0", fontSize: "13px", padding: "20px 0" }}>No upcoming workouts scheduled yet.</div>;
                return dayKeys.map((dk) => {
                  const items = scheduledWorkouts.filter((w) => w.dateKey === dk);
                  if (items.length === 0) return null;
                  const label = dk === today ? "Today" : new Date(dk + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                  return (
                    <div key={dk} style={{ marginBottom: "12px" }}>
                      <div style={{ fontSize: "11px", fontWeight: "bold", color: "#4b6b8a", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "5px" }}>{label}</div>
                      {items.map((item) => (
                        <div key={item.id} style={{ background: "#f8fbff", borderRadius: "12px", padding: "10px 12px", border: "1.5px solid #dbeafe", marginBottom: "6px", position: "relative" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px" }}>{item.workoutName}</div>
                              {item.recurring && <div style={{ fontSize: "10px", color: "#6b8cb0" }}>🔁 Weekly</div>}
                            </div>
                            <button onClick={() => { const tmpl = item.workoutType === "saved" ? savedWorkouts.find((w) => w.id === item.workoutId) : item.workoutType === "ondemand" ? onDemandWorkouts.find((w) => w.id === item.workoutId) : null; setShowScheduleSheet(false); if (tmpl) startWorkout(tmpl); else startBlankWorkout(); }}
                              style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Start</button>
                            <button onClick={() => { setScheduleMoveModal(item); setScheduleMoveDate(item.dateKey); setShowAddForm(false); }}
                              style={{ padding: "5px 8px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>Move</button>
                            {scheduleDeleteConfirm === (item.isRuleOccurrence ? item.id + "_" + item.dateKey : item.id) ? (
                              <div style={{ position: "absolute", left: 0, right: 0, top: 0, background: "#fff", borderRadius: "12px", padding: "10px 12px", border: "1.5px solid #fecaca", zIndex: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#991b1b", marginBottom: "4px" }}>
                                  {item.recurring ? "Remove from schedule?" : "Remove this scheduled workout?"}
                                </div>
                                <div style={{ fontSize: "11px", color: "#6b8cb0", marginBottom: "8px" }}>Your workout history and strength data won't be affected.</div>
                                <div style={{ display: "flex", gap: "6px" }}>
                                  {item.recurring && (
                                    <button onClick={() => deleteScheduleEntry(item, true)} style={{ flex: 1, padding: "6px 4px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>All future</button>
                                  )}
                                  <button onClick={() => deleteScheduleEntry(item, false)} style={{ flex: 1, padding: "6px 4px", borderRadius: "8px", border: "none", background: item.recurring ? "#f97316" : "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>{item.recurring ? "Just this one" : "Yes, remove"}</button>
                                  <button onClick={() => setScheduleDeleteConfirm(null)} style={{ flex: 1, padding: "6px 4px", borderRadius: "8px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => setScheduleDeleteConfirm(item.isRuleOccurrence ? item.id + "_" + item.dateKey : item.id)} style={{ background: "none", border: "none", color: "#f87171", fontSize: "16px", cursor: "pointer", padding: "0 2px" }}>🗑</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
